/**
 * ProtectedRoute Component
 *
 * Route guard that checks authentication and role-based access.
 * Redirects to login if not authenticated, or shows forbidden message
 * if user lacks required role.
 *
 * @module components/ui/ProtectedRoute
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth, type UserRole } from '../../context/AuthContext';

/**
 * Props for ProtectedRoute component.
 */
interface ProtectedRouteProps {
  /** Roles allowed to access this route. If empty, any authenticated user can access. */
  roles?: UserRole[];
  /** Employee categories allowed (only applies when role is EMPLOYEE) */
  categories?: string[];
  /** Children to render (used when not using Outlet) */
  children?: React.ReactNode;
  /** Custom redirect path for unauthorized users */
  redirectTo?: string;
}

/**
 * Loading skeleton shown while checking authentication.
 *
 * @returns Skeleton component
 */
function AuthLoadingSkeleton(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg,#F7F6FD)]">
      <div className="flex flex-col items-center gap-4">
        {/* Logo placeholder */}
        <div className="w-16 h-16 rounded-2xl bg-gray-200 animate-pulse" />
        {/* Text placeholders */}
        <div className="space-y-2">
          <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
          <div className="w-24 h-3 bg-gray-200 rounded animate-pulse mx-auto" />
        </div>
      </div>
    </div>
  );
}

/**
 * Forbidden page shown when user lacks required role.
 *
 * @returns Forbidden component
 */
function ForbiddenPage(): JSX.Element {
  const { logout } = useAuth();

  return (
    <div
      style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg, #F7F6FD)', padding: 16, fontFamily: 'var(--font-body)',
      }}
    >
      <div
        style={{
          maxWidth: 420, width: '100%', textAlign: 'center',
          background: 'var(--surface, #fff)', border: '1.5px solid var(--border, #E4E2F0)',
          borderRadius: 20, padding: '36px 28px', boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        }}
      >
        <div
          style={{
            width: 68, height: 68, margin: '0 auto 20px', borderRadius: 18,
            background: 'var(--red-lt, #FEE2E2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <ShieldAlert size={34} color="var(--red, #DC2626)" />
        </div>

        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: 'var(--t1, #1A1830)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          Access Denied
        </h1>
        <p style={{ fontSize: 14, color: 'var(--t3, #6B6880)', lineHeight: 1.55, margin: '0 0 24px' }}>
          You don&apos;t have permission to access this page. Please contact your
          administrator if you believe this is an error.
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              height: 42, padding: '0 20px', borderRadius: 12,
              border: '1.5px solid var(--border, #E4E2F0)', background: 'var(--surface, #fff)',
              color: 'var(--t2, #3D3A52)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >
            Go Back
          </button>
          <button
            onClick={logout}
            style={{
              height: 42, padding: '0 20px', borderRadius: 12, border: 'none',
              background: 'var(--p, #4F46E5)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ProtectedRoute guards routes based on authentication and role.
 *
 * Behavior:
 * - If loading auth → shows loading skeleton
 * - If not authenticated → redirects to /login
 * - If user must change password → redirects to /change-password
 * - If roles specified and user lacks role → shows forbidden page
 * - Otherwise → renders children or Outlet
 *
 * @example
 * ```tsx
 * // Protect route for any authenticated user
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 *
 * // Protect route for specific roles
 * <Route element={<ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']} />}>
 *   <Route path="/admin/*" element={<AdminDashboard />} />
 * </Route>
 *
 * // With children instead of Outlet
 * <ProtectedRoute roles={['STUDENT']}>
 *   <StudentDashboard />
 * </ProtectedRoute>
 * ```
 *
 * @param props - Component props
 * @returns Protected route content or redirect
 */
export function ProtectedRoute({
  roles = [],
  categories = [],
  children,
  redirectTo = '/login',
}: ProtectedRouteProps): JSX.Element {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return <AuthLoadingSkeleton />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Store current location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect to change password if required
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Check role-based access
  if (roles.length > 0 && !roles.includes(user.role)) {
    return <ForbiddenPage />;
  }

  // Check category-based access for employees
  if (
    categories.length > 0 &&
    user.role === 'EMPLOYEE' &&
    user.employeeCategory &&
    !categories.includes(user.employeeCategory)
  ) {
    return <ForbiddenPage />;
  }

  // Render children or Outlet
  return children ? <>{children}</> : <Outlet />;
}

export default ProtectedRoute;
